import React, { useState, useMemo } from 'react';
import { Button, Icon, Modal, TextArea, TextField, Tooltip, Typography } from '@scale/scaleui';
import moment from 'moment';
import { People } from './People';
import { EditAttendance } from './EditAttendance';
import { SelectSessionButton } from './SelectSessionButton';
import { formatDuration } from 'react-scale/remotasks.com/components/Expert/Onboarding/WebinarSessionCard';
import { ZoomRecordings } from './ZoomRecordings';
import { contructGCalLink } from '../helpers';
import { ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY } from '@scale/shared/webinarSessions/types';
import Link from 'next/link';

interface ViewSessionsModalProps {
  toggleModal: () => void;
  sessions: any[];
}

export const ViewSessionsModal: React.FC<ViewSessionsModalProps> = ({ toggleModal, sessions }) => {
  const futureSessions = useMemo(
    () => sessions.filter(s => moment(s.eventStart).isAfter(moment(Date.now()))),
    [sessions],
  );
  const [currentSession, setCurrentSession] = useState<any>(
    futureSessions.length ? futureSessions[0] : sessions[sessions.length - 1],
  );
  const elapsed = useMemo(
    () =>
      moment(currentSession.eventStart)
        .add(currentSession.duration, 'minutes')
        .isBefore(moment(Date.now())),
    [currentSession.eventStart, currentSession.duration],
  );

  return (
    <Modal
      title="View Session(s)"
      open={true}
      onClose={toggleModal}
      actions={
        <div className="flex items-center gap-2 justify-end">
          <Tooltip
            delayDuration={0}
            side="left"
            text="Only adds this singular session to your Google Calendar"
            target={
              <a href={contructGCalLink(currentSession)} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary">Add to Google Calendar</Button>
              </a>
            }
          />
          <Button onClick={toggleModal}>Close</Button>
        </div>
      }
    >
      <div className="scaleui scale-tailwind flex h-full">
        <div className="h-full border-r border-neutral-200">
          <div className="flex flex-col">
            {sessions.map((s: any) => (
              <SelectSessionButton
                key={s._id}
                onClick={() => setCurrentSession(s)}
                selected={currentSession?._id === s?._id}
                title={`${moment(s.eventStart).format('dddd MMM D, YYYY [at] h[:]mmA')}${
                  s.canceled ? ' (Canceled)' : ''
                }`}
                elapsed={moment(s.eventStart).isBefore(moment(Date.now()))}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 px-2">
          <div className="flex items-end">
            <Typography.H4 className="mr-2">
              {moment(currentSession?.eventStart).format('dddd MMM D, YYYY [at] h[:]mmA')}
              {currentSession?.canceled && ' (Canceled)'}
            </Typography.H4>

            <Typography.Paragraph2 className="ml-auto">
              Duration: {formatDuration(currentSession?.duration)}
            </Typography.Paragraph2>
          </div>
          <TextField
            label="Name"
            placeholder="Enter session name"
            value={currentSession?.name ?? ''}
            onChange={() => {}}
            disabled={true}
          />
          <div className="flex gap-1">
            <Typography.Subtitle3>ID:</Typography.Subtitle3>
            <Typography.Paragraph2>{currentSession._id}</Typography.Paragraph2>
          </div>
          {!elapsed && !currentSession?.canceled && currentSession?.zoomMeeting?.joinUrl && (
            <Link
              href={currentSession?.zoomMeeting?.joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 flex items-center"
            >
              Join Meeting
              <Icon icon="external-link" size="sm" variant="primary" />
            </Link>
          )}
          {!elapsed && (
            <div className="flex gap-1">
              <Typography.Subtitle3>Zoom Host Key:</Typography.Subtitle3>
              <Typography.Paragraph2>
                {
                  ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY[
                    (currentSession?.zoomMeeting?.host as string) ?? 'training@outlier.ai'
                  ]
                }
              </Typography.Paragraph2>
            </div>
          )}
          {elapsed && <ZoomRecordings session={currentSession} />}
          <People
            invitedUsers={currentSession?.invitedUsers}
            setInvitedUsers={() => {}}
            audience={currentSession?.audience}
            audienceBucket={currentSession?.audienceBucket}
            invitedWorkerTeams={currentSession?.invitedWorkerTeams}
            setInvitedWorkerTeams={() => {}}
            invitedQualifications={currentSession?.invitedQualifications}
            setInvitedQualifications={() => {}}
            invitedSquads={currentSession?.invitedSquads}
            setInvitedSquads={() => {}}
            hostedBy={currentSession?.hostedBy}
            setHostedBy={() => {}}
            rsvpUsers={currentSession?.rsvpUsers}
            projectId={currentSession?.project}
            disabled
          />
          <TextField
            label="Max RSVPs (optional)"
            type="number"
            className="w-[180px]"
            value={currentSession?.rsvpMax ?? ''}
            onChange={() => {}}
            disabled
          />
          <TextField
            label="Min Attendance Duration in Minutes (optional)"
            type="number"
            className="w-[360px]"
            value={currentSession?.minAttendanceDuration ?? ''}
            onChange={() => {}}
            disabled
          />
          {currentSession?.project && (
            <>
              <Typography.Subtitle3 className="text-neutral-600">Project</Typography.Subtitle3>
              <Typography.Paragraph3>{currentSession.project}</Typography.Paragraph3>
            </>
          )}
          <TextArea
            label="Description (optional)"
            placeholder="Enter session description"
            value={currentSession?.description ?? ''}
            onChange={() => {}}
            disabled
          />
          {elapsed && (
            <EditAttendance
              session={currentSession}
              attendees={currentSession?.attendees}
              setAttendees={() => {}}
              disabled
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
